import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, generateReport, getReportsByGroup } from '@philips-emr/hpms-provider-commons';
import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { ActionAPI, PanelAPI, Text } from '@philips-emr/tws-components';
import Controller, { OnAfterFetch, OnClick, OnDynamicContentRender, OnMount } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { CnesDetailAliases as Aliases, FragmentNames } from '../codegen';

import BaseController from './BaseController';
import { CnesSearch, getStampStatusCnes, StatusCnesSearch } from './cnes-search-controller';

const CNES_SEARCH = '35';

interface InconsistencyCnes {
  dsInconsistency: string,
}

@Controller({ fragment: FragmentNames.CNES_DETAIL })
class CnesDetailController extends BaseController {

  @Inject(Aliases.ACTION_PRINT)
  private actionPrint: ActionAPI;
  @Inject(Aliases.PANEL_TITLE_CNES_INCONSISTENCIES)
  private panelTitleCnesInconsistencies: PanelAPI;

  @OnMount(Aliases.PANEL_CNES_SEARCH_DETAIL)
  onMountPanelCnesSearchDetail(): void {
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const { nrSeqCnesSearch } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqProvider, nrSeqCnesSearch });
  }

  @OnAfterFetch(Aliases.FETCH_CNES_SEARCH_DETAIL)
  async onAfterFetchInconsistenciesCnes(event: FetchEvent): Promise<void> {
    const data = event.data;
    if(data) {
      const { ieStatus } = data as CnesSearch;
      await getReportsByGroup(CNES_SEARCH).then(resp => {
        if (resp.length > 0) {
          this.actionPrint.setEnabled(ieStatus !== StatusCnesSearch.ONGOING_CONSULTATION);
        }
        return resp;
      });
    }
  }

  @OnAfterFetch(Aliases.FETCH_INCONSISTENCIES_CNES)
  onAfterFetchInconsistencies(event: FetchEvent): void {
    const data = event.data;
    if(!isEmptyArrayOrObject(data)) {
      this.panelTitleCnesInconsistencies.setVisible(true);
    }
  }

  @OnClick(Aliases.ACTION_PRINT)
  onClickActionPrint(): void {
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const { nrSeqCnesSearch } = this.historyService.getCurrentRouteParams() as unknown as CnesSearch;

    const requestParams = {
      nrSeqProvider,
    };

    const reportParams = {
      nrSeqCnesSearch,
    };

    generateReport(Endpoint.POST.CNES_SEARCH_REPORT, requestParams, CNES_SEARCH, undefined, reportParams)
      .finally(() => {
        this.changeCursor(false);
      });
  }

  private changeCursor(isProgress: boolean): void {
    document.body.style.cursor = isProgress ? 'wait' : 'default';
  }

  @OnDynamicContentRender(Aliases.FIELD_STATUS)
  onDynamicContentRenderFieldStatus(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsStatus } = data as unknown as CnesSearch;
    const stampVisit = getStampStatusCnes(ieStatus, dsStatus);
    if (stampVisit) {
      render((): ReactElement => stampVisit);
    }
  }

  @OnDynamicContentRender(Aliases.FIELD_INCONSISTENCIES_CNES)
  onDynamicContentRenderFieldInconsistenciesCnes(event: RenderEvent): void {
    const { data, render } = event;
    if (!isEmptyArrayOrObject(data)) {
      const { dsInconsistency } = data as InconsistencyCnes;
      render((): ReactElement =>
        <Text value={ dsInconsistency } />
      );
    }
  }
}

export {
  CnesDetailController,
};
